import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { CalendarRange, Plus } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { Dialog, DialogTrigger } from "@/components/ui/dialog";
import { AcademicYearForm } from "@/components/academic-years/AcademicYearForm";
import { EditAcademicYearForm } from "@/components/academic-years/EditAcademicYearForm";
import { AcademicYearList } from "@/components/academic-years/AcademicYearList";
import { Header } from "@/components/layout/Header";

export default function AcademicYears() {
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedYear, setSelectedYear] = useState<any>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: academicYears, refetch } = useQuery({
    queryKey: ["academicYears"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("academic_years")
        .select(`
          *,
          terms (
            id,
            name,
            start_date,
            end_date,
            is_active
          )
        `)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data;
    },
  });

  const handleCreateOpenChange = (open: boolean) => {
    setCreateDialogOpen(open);
  };

  const handleEditOpenChange = (open: boolean) => {
    setEditDialogOpen(open);
    if (!open) {
      setSelectedYear(null);
    }
  };

  const setActiveYear = async (yearId: string) => {
    try {
      const { error } = await supabase.rpc("set_active_academic_year", {
        year_id: yearId,
      });

      if (error) throw error;

      // Invalidate and refetch the query to get fresh data
      await queryClient.invalidateQueries({ queryKey: ["academicYears"] });
      await refetch();

      toast({
        title: "Success",
        description: "Academic year status updated",
      });
    } catch (error: any) {
      console.error("Error setting active year:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to update academic year status",
        variant: "destructive",
      });
    }
  };

  const setActiveTerm = async (termId: string) => {
    try {
      const { error } = await supabase.rpc("set_active_term", {
        term_id: termId,
      });

      if (error) throw error;

      // Invalidate and refetch the query to get fresh data
      await queryClient.invalidateQueries({ queryKey: ["academicYears"] });
      await refetch();

      toast({
        title: "Success",
        description: "Term status updated",
      });
    } catch (error: any) {
      console.error("Error setting active term:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to update term status",
        variant: "destructive",
      });
    }
  };

  const handleEdit = (year: any) => {
    setSelectedYear(year);
    setEditDialogOpen(true);
  };

  const handleDelete = async (yearId: string) => {
    try {
      // First delete associated terms
      const { error: termsError } = await supabase
        .from('terms')
        .delete()
        .eq('academic_year_id', yearId);

      if (termsError) throw termsError;

      // Then delete the academic year
      const { error: yearError } = await supabase
        .from('academic_years')
        .delete()
        .eq('id', yearId);

      if (yearError) throw yearError;

      toast({
        title: "Success",
        description: "Academic year deleted successfully",
      });
      
      await queryClient.invalidateQueries({ queryKey: ["academicYears"] });
      await refetch();
    } catch (error: any) {
      console.error("Error deleting academic year:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to delete academic year",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <div className="container px-4 py-6 mt-[147px]">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <CalendarRange className="h-6 w-6" />
            <h1 className="text-2xl font-bold">Academic Years</h1>
          </div>
          <Dialog open={createDialogOpen} onOpenChange={handleCreateOpenChange}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="mr-2 h-4 w-4" />
                New Academic Year
              </Button>
            </DialogTrigger>
            <AcademicYearForm
              onSuccess={refetch}
              onClose={() => setCreateDialogOpen(false)}
            />
          </Dialog>
        </div>

        <Dialog open={editDialogOpen} onOpenChange={handleEditOpenChange}>
          {selectedYear && (
            <EditAcademicYearForm
              academicYear={selectedYear}
              onSuccess={refetch}
              onClose={() => setEditDialogOpen(false)}
            />
          )}
        </Dialog>

        <AcademicYearList
          academicYears={academicYears || []}
          onSetActive={setActiveYear}
          onSetActiveTerm={setActiveTerm}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      </div>
    </div>
  );
}